import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { collection, getDocs, getFirestore } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowsRotate,
  faChartLine,
  faMagnifyingGlass,
  faUsers
} from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']

const convertedStatusSet = new Set(convertedStatuses.map((value) => String(value).trim().toLowerCase()))
const notConvertedStatusSet = new Set(notConvertedStatuses.map((value) => String(value).trim().toLowerCase()))

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const normalizeText = (value) => String(value || '').trim()
const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const getYearNumber = (year) => Number.parseInt(year, 10) || 0

const seriesKey = (source, kind) => `${source}__${kind}`

const sourceColor = (index) => `hsl(${(index * 53) % 360}, 68%, 42%)`
const sourceTint = (index) => `hsl(${(index * 53) % 360}, 68%, 68%)`

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="trendsOverTimeTooltip">
      <strong>{label}</strong>
      <div className="trendsOverTimeTooltipMeta">
        <span>Converted</span>
        <strong>{formatCount(row.convertedTotal)}</strong>
      </div>
      <div className="trendsOverTimeTooltipMeta">
        <span>Not converted</span>
        <strong>{formatCount(row.notConvertedTotal)}</strong>
      </div>
      <div className="trendsOverTimeTooltipBody">
        {payload.map((entry) => (
          <div className="trendsOverTimeTooltipRow" key={entry.dataKey}>
            <span>
              <i style={{ background: entry.color }} />
              {entry.name}
            </span>
            <strong>{formatCount(entry.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function TrendsOverTime() {
  const db = getFirestore(app)
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [selectedYear, setSelectedYear] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const querySnapshot = await getDocs(collection(db, 'users'))
      const items = []

      querySnapshot.forEach((docItem) => {
        const data = docItem.data()
        const createdAt = data.appDate?.toDate ? data.appDate.toDate() : null

        if (!createdAt) {
          return
        }

        const source = normalizeText(data.source) || 'Unknown'
        const status = normalizeStatus(data.userStatus)
        let statusGroup = null

        if (convertedStatusSet.has(status)) {
          statusGroup = 'converted'
        } else if (notConvertedStatusSet.has(status)) {
          statusGroup = 'notConverted'
        }

        items.push({
          source,
          statusGroup,
          year: String(createdAt.getFullYear()),
          yearNumber: createdAt.getFullYear(),
          monthKey: `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`,
          monthLabel: createdAt.toLocaleString('en-US', { month: 'short', year: 'numeric' })
        })
      })

      setRecords(items)
    } catch (fetchError) {
      console.error('Failed to load trends over time analysis:', fetchError)
      setRecords([])
      setError('We could not load the trends page right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const sourceTotals = useMemo(() => {
    const totals = new Map()

    records.forEach((record) => {
      if (!record.statusGroup) {
        return
      }

      if (!totals.has(record.source)) {
        totals.set(record.source, {
          source: record.source,
          converted: 0,
          notConverted: 0
        })
      }

      const bucket = totals.get(record.source)

      if (record.statusGroup === 'converted') {
        bucket.converted += 1
      } else {
        bucket.notConverted += 1
      }
    })

    return Array.from(totals.values())
      .map((row) => ({
        ...row,
        total: row.converted + row.notConverted,
        conversionRate: row.converted + row.notConverted > 0 ? (row.converted / (row.converted + row.notConverted)) * 100 : 0
      }))
      .sort((first, second) => {
        if (second.total !== first.total) {
          return second.total - first.total
        }

        return first.source.localeCompare(second.source)
      })
  }, [records])

  const yearTotals = useMemo(() => {
    const totals = new Map()

    records.forEach((record) => {
      if (!record.statusGroup) {
        return
      }

      if (!totals.has(record.year)) {
        totals.set(record.year, {
          year: record.year,
          yearNumber: record.yearNumber,
          converted: 0,
          notConverted: 0
        })
      }

      const bucket = totals.get(record.year)

      if (record.statusGroup === 'converted') {
        bucket.converted += 1
      } else {
        bucket.notConverted += 1
      }
    })

    return Array.from(totals.values())
      .map((row) => ({
        ...row,
        total: row.converted + row.notConverted,
        conversionRate: row.converted + row.notConverted > 0 ? (row.converted / (row.converted + row.notConverted)) * 100 : 0
      }))
      .sort((first, second) => first.yearNumber - second.yearNumber)
  }, [records])

  const availableYears = useMemo(() => yearTotals.map((row) => row.year), [yearTotals])

  useEffect(() => {
    if (!availableYears.length) {
      if (selectedYear) {
        setSelectedYear('')
      }
      return
    }

    const latestYear = availableYears[availableYears.length - 1]

    if (!selectedYear || !availableYears.includes(selectedYear)) {
      setSelectedYear(latestYear)
    }
  }, [availableYears, selectedYear])

  const chartSourceKeys = useMemo(() => {
    const leaders = sourceTotals.slice(0, 5).map((row) => row.source)
    const hasOther = sourceTotals.length > leaders.length

    if (hasOther) {
      return [...leaders, 'Other sources']
    }

    return leaders
  }, [sourceTotals])

  const chartSeries = useMemo(() => {
    return chartSourceKeys.map((source, index) => ({
      source,
      convertedKey: seriesKey(source, 'converted'),
      notConvertedKey: seriesKey(source, 'notConverted'),
      convertedColor: sourceColor(index),
      notConvertedColor: sourceTint(index)
    }))
  }, [chartSourceKeys])

  const chartData = useMemo(() => {
    const rowsByYear = new Map()
    const chartSourceSet = new Set(chartSourceKeys.filter((source) => source !== 'Other sources'))
    const hasOtherSources = chartSourceKeys.includes('Other sources')

    records.forEach((record) => {
      if (!record.statusGroup) {
        return
      }

      const source = chartSourceSet.has(record.source)
        ? record.source
        : (hasOtherSources ? 'Other sources' : record.source)

      if (!rowsByYear.has(record.year)) {
        rowsByYear.set(record.year, {
          year: record.year,
          yearNumber: record.yearNumber,
          convertedTotal: 0,
          notConvertedTotal: 0
        })
      }

      const row = rowsByYear.get(record.year)
      const convertedKey = seriesKey(source, 'converted')
      const notConvertedKey = seriesKey(source, 'notConverted')

      if (!row[convertedKey]) {
        row[convertedKey] = 0
      }

      if (!row[notConvertedKey]) {
        row[notConvertedKey] = 0
      }

      if (record.statusGroup === 'converted') {
        row[convertedKey] += 1
        row.convertedTotal += 1
      } else {
        row[notConvertedKey] += 1
        row.notConvertedTotal += 1
      }
    })

    return Array.from(rowsByYear.values()).sort((first, second) => first.yearNumber - second.yearNumber)
  }, [chartSourceKeys, records])

  const topSource = sourceTotals[0] || null
  const bestYear = useMemo(() => {
    return [...yearTotals].sort((first, second) => second.conversionRate - first.conversionRate)[0] || null
  }, [yearTotals])
  const worstYear = useMemo(() => {
    return [...yearTotals].sort((first, second) => first.conversionRate - second.conversionRate)[0] || null
  }, [yearTotals])
  const selectedYearSummary = useMemo(() => {
    return yearTotals.find((row) => row.year === selectedYear) || null
  }, [selectedYear, yearTotals])

  const selectedYearSourceRows = useMemo(() => {
    if (!selectedYear) {
      return []
    }

    const filtered = records.filter((record) => record.year === selectedYear && record.statusGroup)
    const rowsBySource = new Map()

    filtered.forEach((record) => {
      if (!rowsBySource.has(record.source)) {
        rowsBySource.set(record.source, {
          source: record.source,
          converted: 0,
          notConverted: 0
        })
      }

      const bucket = rowsBySource.get(record.source)

      if (record.statusGroup === 'converted') {
        bucket.converted += 1
      } else {
        bucket.notConverted += 1
      }
    })

    return Array.from(rowsBySource.values())
      .map((row) => ({
        ...row,
        total: row.converted + row.notConverted,
        conversionRate: row.converted + row.notConverted > 0 ? (row.converted / (row.converted + row.notConverted)) * 100 : 0
      }))
      .filter((row) => row.total > 0)
      .sort((first, second) => {
        if (second.total !== first.total) {
          return second.total - first.total
        }

        return first.source.localeCompare(second.source)
      })
  }, [records, selectedYear])

  const filteredSelectedYearSources = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return selectedYearSourceRows
    }

    return selectedYearSourceRows.filter((row) => row.source.toLowerCase().includes(search))
  }, [searchTerm, selectedYearSourceRows])

  const selectedYearTopSource = filteredSelectedYearSources[0] || selectedYearSourceRows[0] || null
  const hasChartData = chartData.length > 0
  const hasSourceRows = filteredSelectedYearSources.length > 0
  const overallConverted = yearTotals.reduce((sum, row) => sum + row.converted, 0)
  const overallNotConverted = yearTotals.reduce((sum, row) => sum + row.notConverted, 0)
  const overallRate = overallConverted + overallNotConverted > 0
    ? (overallConverted / (overallConverted + overallNotConverted)) * 100
    : 0

  const emptyMessage = records.length
    ? 'No sources match the current filters.'
    : 'No records with a valid application date were found yet.'

  return (
    <div className="mainContainer trendsOverTimePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="trendsOverTimeContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Trends Over Time</h4>
              <p className="appsSubtext">
                See how conversions and drop-offs move year by year, then drill into the selected year for source-level detail.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Tracked records</span>
                <strong>{formatCount(yearTotals.reduce((sum, row) => sum + row.total, 0))}</strong>
              </div>
              <div className="appsStatChip">
                <span>Converted</span>
                <strong>{formatCount(overallConverted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Not converted</span>
                <strong>{formatCount(overallNotConverted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Conversion rate</span>
                <strong>{formatPercent(overallRate)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard trendsOverTimeFiltersCard">
            <div className="appsSearch input-group trendsOverTimeSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search source..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="trendsOverTimeYearControl">
              <span className="trendsOverTimeControlLabel">Selected year</span>
              <Form.Select
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
              >
                {availableYears.length ? (
                  availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))
                ) : (
                  <option value="">No years available</option>
                )}
              </Form.Select>
            </div>

            <div className="trendsOverTimeFiltersMeta">
              <span>{formatCount(chartSourceKeys.length)} chart series</span>
              <span>{formatCount(selectedYearSourceRows.length)} sources in selected year</span>
            </div>

            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="trendsOverTimeSummaryGrid">
            <div className="trendsOverTimeSummaryCard">
              <span>Selected year</span>
              <strong>{selectedYear || '—'}</strong>
              <p>{selectedYearSummary ? `${formatCount(selectedYearSummary.total)} tracked records` : 'No year selected yet.'}</p>
            </div>
            <div className="trendsOverTimeSummaryCard">
              <span>Selected year conversion</span>
              <strong>{selectedYearSummary ? formatPercent(selectedYearSummary.conversionRate) : '0.0%'}</strong>
              <p>{selectedYearSummary ? `${formatCount(selectedYearSummary.converted)} converted` : 'No data yet.'}</p>
            </div>
            <div className="trendsOverTimeSummaryCard">
              <span>Top source</span>
              <strong>{topSource ? topSource.source : '—'}</strong>
              <p>{topSource ? `${formatCount(topSource.total)} records overall` : 'No source data yet.'}</p>
            </div>
            <div className="trendsOverTimeSummaryCard">
              <span>Best year</span>
              <strong>{bestYear ? bestYear.year : '—'}</strong>
              <p>{bestYear ? formatPercent(bestYear.conversionRate) : 'No data yet.'}</p>
            </div>
          </div>

          <div className="trendsOverTimeGrid">
            <div className="appsTableCard trendsOverTimeChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  Yearly trend by source
                </h6>
                <span>
                  {chartSourceKeys.length
                    ? `${chartSourceKeys.slice(0, 5).join(', ')}${chartSourceKeys.length > 5 ? ' + others' : ''}`
                    : 'No series available'}
                </span>
              </div>

              <div className="trendsOverTimeChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading trends over time...</span>
                    </Spinner>
                    <p>Loading trends over time...</p>
                  </div>
                ) : hasChartData ? (
                  <div className="trendsOverTimeChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="year"
                          tick={{ fontSize: 12, fill: '#475569' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <Tooltip content={<TrendTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={32}
                          iconType="circle"
                          wrapperStyle={{ fontSize: '12px', color: '#64748b' }}
                        />
                        {chartSeries.map((series) => (
                          <Fragment key={series.source}>
                            <Line
                              type="monotone"
                              dataKey={series.convertedKey}
                              name={`${series.source} converted`}
                              stroke={series.convertedColor}
                              strokeWidth={2.5}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey={series.notConvertedKey}
                              name={`${series.source} not converted`}
                              stroke={series.notConvertedColor}
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={false}
                            />
                          </Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="appsInsightsCard trendsOverTimeHighlightsCard">
            <div className="appsInsightsHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Trend highlights
              </h6>
              <span>{formatCount(yearTotals.length)} years tracked</span>
            </div>

            <div className="trendsOverTimeHighlightsGrid">
              <div className="trendsOverTimeMiniStats">
                <div className="trendsOverTimeMiniStat">
                  <span>Selected year leader</span>
                  <strong>{selectedYearTopSource ? selectedYearTopSource.source : '—'}</strong>
                  <small>{selectedYearTopSource ? formatCount(selectedYearTopSource.total) : 'No data'}</small>
                </div>
                <div className="trendsOverTimeMiniStat">
                  <span>Strongest year</span>
                  <strong>{bestYear ? bestYear.year : '—'}</strong>
                  <small>{bestYear ? formatPercent(bestYear.conversionRate) : 'No data'}</small>
                </div>
              </div>

              <div className="appsInsightsList">
                {sourceTotals.length ? (
                  sourceTotals.slice(0, 5).map((row) => (
                    <div className="appsInsightsItem" key={row.source}>
                      <div>
                        <strong>{row.source}</strong>
                        <span>{formatCount(row.total)} tracked records</span>
                      </div>
                      <small>{formatPercent(row.conversionRate)} converted</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No source highlights match the current data.</p>
                  </div>
                )}
              </div>
            </div>

            <p className="appsInsightsMore">
              {bestYear && worstYear
                ? `Best year: ${bestYear.year} at ${formatPercent(bestYear.conversionRate)}. Weakest year: ${worstYear.year} at ${formatPercent(worstYear.conversionRate)}.`
                : 'Refresh after new records are added to update the trend highlights.'}
            </p>
          </div>

          <div className="appsTableCard trendsOverTimeTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Source breakdown for {selectedYear || 'selected year'}
              </h6>
              <span>{formatCount(filteredSelectedYearSources.length)} results</span>
            </div>

            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading trend breakdown...</span>
                  </Spinner>
                  <p>Loading trend breakdown...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable trendsOverTimeTable">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Converted</th>
                      <th>Not Converted</th>
                      <th>Total</th>
                      <th>Conversion Rate</th>
                      <th>Share</th>
                      <th>Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasSourceRows ? (
                      filteredSelectedYearSources.map((row) => {
                        const share = selectedYearSummary?.total > 0 ? (row.total / selectedYearSummary.total) * 100 : 0
                        const topTotal = filteredSelectedYearSources[0]?.total || 0
                        const progress = topTotal > 0 ? (row.total / topTotal) * 100 : 0

                        return (
                          <tr key={row.source}>
                            <td>
                              <div className="trendsOverTimeRowLabel">
                                <strong>{row.source}</strong>
                                <span>{row.total > 0 ? 'Tracked source' : 'No records'}</span>
                              </div>
                            </td>
                            <td>{formatCount(row.converted)}</td>
                            <td>{formatCount(row.notConverted)}</td>
                            <td>
                              <span className="trendsOverTimeTotalCell">{formatCount(row.total)}</span>
                            </td>
                            <td>{formatPercent(row.conversionRate)}</td>
                            <td>{formatPercent(share)}</td>
                            <td>
                              <div className="trendsOverTimeTrack" aria-hidden="true">
                                <span
                                  className="trendsOverTimeTrackFill"
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrendsOverTime
